import React, { useCallback, useEffect, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Typography, Alert, IconButton, Tooltip, Chip, CircularProgress } from '@mui/material';
import InvButton from '../InvButton';
import { getWorkOrderPortalLink, updateWorkOrder } from '../../api/workOrder';
import { woIsBid } from '../../utils/woEntity';
import { formatDateTime } from '../../utils';
import { IPD_COLORS } from '../../theme';

export default function WoPortalLinkCard({ workOrder, onUpdated }) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const issued = workOrder?.status === 'ISSUED';
  const publishedBid = woIsBid(workOrder) && ['PUBLISHED', 'AWARDED'].includes(workOrder?.status);
  const showLink = publishedBid || issued;

  const load = useCallback(async () => {
    if (!workOrder?.id || !showLink) return;
    setLoading(true);
    setError(null);
    try {
      setLink(await getWorkOrderPortalLink(workOrder.id));
    } catch (e) {
      setError(e.message || 'Could not build the vendor link');
    } finally {
      setLoading(false);
    }
  }, [workOrder?.id, showLink]);

  useEffect(() => { load(); }, [load]);

  const rawUrl = link
    ? (link.url || (link.token && `${window.location.origin}/vendor-portal/${link.token}`))
    : '';
  const openBid = Boolean(workOrder?.is_open_bid) && publishedBid;

  async function copy() {
    if (!rawUrl) return;
    try {
      await navigator.clipboard.writeText(rawUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — select the link and copy it manually.');
    }
  }

  async function toggleOpenBid() {
    try {
      await updateWorkOrder(workOrder.id, { is_open_bid: !workOrder.is_open_bid });
      onUpdated?.();
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  if (!showLink) {
    return (
      <Box sx={{ p: 2, borderRadius: '10px', border: `1px dashed ${IPD_COLORS.border}` }}>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
          The vendor link becomes available once this work bid is published.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: 2, mb: 2, borderRadius: '10px',
      border: `1px solid ${IPD_COLORS.border}`, backgroundColor: IPD_COLORS.cardBg,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: IPD_COLORS.textPrimary }}>
          Vendor link
        </Typography>
        <Chip
          size="small"
          label={issued
            ? 'Sign in to accept or reject'
            : (openBid ? 'Open bid — new vendors can register' : 'Invited vendors only')}
          sx={{
            height: 20, fontSize: 11, fontWeight: 600,
            backgroundColor: openBid ? IPD_COLORS.success.bg : IPD_COLORS.selectedBg,
            color: openBid ? IPD_COLORS.success.text : IPD_COLORS.textSecondary,
          }}
        />
      </Box>

      {loading && <CircularProgress size={20} sx={{ color: IPD_COLORS.primary }} />}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {rawUrl && (
        <>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            p: 1, borderRadius: '8px', backgroundColor: IPD_COLORS.selectedBg,
          }}>
            <Typography sx={{
              flex: 1, fontSize: 12, fontFamily: 'monospace',
              color: IPD_COLORS.textPrimary, wordBreak: 'break-all',
            }}>
              {rawUrl}
            </Typography>
            <Tooltip title={copied ? 'Copied' : 'Copy link'}>
              <IconButton size="small" onClick={copy} aria-label="Copy vendor link">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open as a vendor would see it">
              <IconButton
                size="small"
                component="a"
                href={rawUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open vendor link"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 1 }}>
            {issued
              ? 'The issued vendor signs in with OTP on this link to accept or reject the work order.'
              : (openBid
                ? 'Invited vendors are emailed. Anyone with this URL can register, verify OTP, and quote.'
                : 'Only invited vendors can quote. Turn on Open bid below if unregistered vendors should register here.')}
            {link?.expires_at && ` Active until ${formatDateTime(link.expires_at)}.`}
          </Typography>
          {publishedBid && (
            <Box sx={{ mt: 1.5 }}>
              <InvButton
                size="small"
                variant={openBid ? 'outlined' : undefined}
                tone={openBid ? 'teal' : 'mint'}
                onClick={toggleOpenBid}
              >
                {openBid ? 'Switch to invited vendors only' : 'Allow new vendors to register'}
              </InvButton>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
